import _, { parseInt } from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import NewsletterGql from '@/extensions/newsletter/store/graphql/queries/Newsletter.gql';
import NewslettersPaginatedGql from '@/extensions/newsletter/store/graphql/queries/NewslettersPaginated.gql';
import SentEmailsPaginatedGql from '@/extensions/newsletter/store/graphql/queries/SentEmailsPaginated.gql';
import SentEmailGql from '@/extensions/newsletter/store/graphql/queries/SentEmail.gql';
import SendNewsletterGql from '@/extensions/newsletter/store/graphql/mutations/SendNewsletter.gql';
import CreateNewsletterGql from '@/extensions/newsletter/store/graphql/mutations/CreateNewsletter.gql';
import UpdateNewsletterGql from '@/extensions/newsletter/store/graphql/mutations/UpdateNewsletter.gql';
import DeleteNewsletterGql from '@/extensions/newsletter/store/graphql/mutations/DeleteNewsletter.gql';
import CancelPlannedNewsletterGql from '@/extensions/newsletter/store/graphql/mutations/CancelPlannedNewsletter.gql';
import ReschedulePlannedNewsletterGql from '@/extensions/newsletter/store/graphql/mutations/ReschedulePlannedNewsletter.gql';

export default {
    fetchNewslettersPaginated: data =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .query({
                    query: NewslettersPaginatedGql,
                    variables: {
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'created_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                        link: _.get(data, 'link.route', '/marketing/newsletter/'),
                        filters
                    }
                })
                .then(({ data }) => resolve(data.EX_NWSLTR_NewslettersPaginated))
                .catch(err => reject(err));
        }),
    fetchNewsletter: (id, cmsID = null, skipPage = false, skipSubscriptionTopics = false) => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: NewsletterGql,
                    variables: {
                        id: parseInt(id),
                        cms: parseInt(cmsID),
                        skipPage,
                        skipSubscriptionTopics
                    },
                    fetchPolicy: (skipPage || skipSubscriptionTopics) ? 'network-only' : 'cache-first'
                })
                .then(({ data }) => resolve(data.EX_NWSLTR_Newsletter))
                .catch(err => reject(err));
        });
    },
    send: (variables) => {
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SendNewsletterGql,
                    variables
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_NWSLTR_SendNewsletter);
                })
                .catch(err => reject(err));
        });
    },
    create: newsletter => {
        if (newsletter === undefined) throw new Error('required argument is missing');

        newsletter = _.removeDeepByKey(newsletter, '__typename');
        // Remove client-side only fields
        delete newsletter.calculatedRecipientCount;
        delete newsletter.calculatedRecipientCountLoading;
        // Remove read-only relationship fields (these are populated by the server)
        delete newsletter.sentByUser;
        delete newsletter.sentFromPos;
        delete newsletter.sourceNewsletter;
        delete newsletter.status;
        delete newsletter.sentEmailsCount;
        delete newsletter.statusLog;
        delete newsletter.start_sending_at;
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateNewsletterGql,
                    variables: { newsletter }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_NWSLTR_CreateNewsletter);
                })
                .catch(err => reject(err));
        });
    },
    update: newsletter => {
        if (newsletter === undefined) throw new Error('required argument is missing');

        newsletter = _.removeDeepByKey(newsletter, '__typename');
        // Remove client-side only fields
        delete newsletter.calculatedRecipientCount;
        delete newsletter.calculatedRecipientCountLoading;
        // Remove read-only relationship fields (these are populated by the server)
        delete newsletter.sentByUser;
        delete newsletter.sentFromPos;
        delete newsletter.sourceNewsletter;
        delete newsletter.status;
        delete newsletter.sentEmailsCount;
        delete newsletter.statusLog;
        delete newsletter.start_sending_at;
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateNewsletterGql,
                    variables: { newsletter }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_NWSLTR_UpdateNewsletter);
                })
                .catch(err => reject(err));
        });
    },
    delete: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteNewsletterGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_NWSLTR_DeleteNewsletter);
                })
                .catch(err => reject(err));
        });
    },
    cancelPlanned: (id) => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CancelPlannedNewsletterGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_NWSLTR_CancelPlannedNewsletter);
                })
                .catch(err => reject(err));
        });
    },
    reschedulePlanned: (id, scheduledAt) => {
        if (id === undefined) throw new Error('required argument is missing');
        if (scheduledAt === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ReschedulePlannedNewsletterGql,
                    variables: { id: parseInt(id), scheduled_at: parseInt(scheduledAt) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_NWSLTR_ReschedulePlannedNewsletter);
                })
                .catch(err => reject(err));
        });
    },
    /**
     * Fetch the HTML content preview of a newsletter from its first sent email
     * @param {number} newsletterId - The newsletter ID
     * @returns {Promise<string|null>} - The HTML content or null if not available
     */
    fetchNewsletterPreview: async (newsletterId) => {
        if (newsletterId === undefined) throw new Error('required argument is missing');

        // First, get the first sent email ID for this newsletter
        const { data: paginatedData } = await apolloClient.query({
            query: SentEmailsPaginatedGql,
            variables: {
                newsletter_id: parseInt(newsletterId),
                currentPage: 1,
                perPage: 1,
                order: 'created_at',
                direction: 'asc',
                filters: [{ key: 'is_test', match: false }]
            },
            fetchPolicy: 'network-only'
        });

        const items = _.get(paginatedData, 'EX_NWSLTR_SentEmailsPaginated.items', []);
        if (!items.length) {
            return null;
        }

        const sentEmailId = parseInt(items[0].id);

        // Fetch the full sent email with content
        const { data: emailData } = await apolloClient.query({
            query: SentEmailGql,
            variables: { id: sentEmailId },
            fetchPolicy: 'network-only'
        });

        return _.get(emailData, 'EX_NWSLTR_SentEmail.content', null);
    }
};
